<template>
  <div>

    <!-- BUTTON -->
    <a class="button is-dark is-outlined"
      @click="openModal"
      style="margin-bottom:20px"
      :disabled="chosenPages.length > 9">
      <span class="icon"><i class="fas fa-plus"></i></span>
      <strong>Seiten hinzufügen ({{ chosenPages.length }}/10)</strong>
    </a>


    <draggable v-model="chosenPages" :options="{handle:'.draghandle'}" @change="updateOrder">
      <div
        class="box"
        :class="statusClass(index)"
        v-for="(page, index) in chosenPages">
        <div class="columns is-vcentered has-background-light">

          <!-- DRAG HANDLE -->
          <div class="column is-narrow">
            <a class="button draghandle is-primary">
              <span class="icon"><i class="fas fa-grip-lines"></i></span>
            </a>
          </div>

          <div class="column is-narrow">
            <a class="button is-link" :href="`/dashboard/seiten/edit/${page._id}`" target="_blank">
              <span class="icon"><i class="fas fa-edit"></i></span>
            </a>
          </div>

          <!-- PAGE POSITION IN CHANNEL -->
          <div class="column is-narrow">
            <strong>{{ index + 1 }}</strong>
          </div>

          <!-- NAME & DESCRIPTION -->
          <div class="column">
            <p><strong>{{ page.meta.name }}</strong></p>
            <p>{{ page.meta.description }}</p>
            <p v-if="pageSchedules && pageSchedules[index]" class="help">
              <span v-if="isExpired(index)" class="tag is-danger is-light">Abgelaufen</span>
              <span v-if="isPending(index)" class="tag is-warning is-light">Geplant</span>
              <span v-if="summary(index)" class="has-text-grey">
                <i class="fas fa-clock"></i> {{ summary(index) }}
              </span>
            </p>
          </div>

          <!-- PAGE DURATION -->
          <div class="column is-narrow" >
            <div class="field">
              <label class="help label">Anzeige (Sek.)</label>
              <div class="control" >
                <input class="input" type="number" v-model.number="pageDurations[index]" style="max-width:170px">
              </div>
            </div>
          </div>

          <!-- PAGE SCHEDULE TOGGLE -->
          <div class="column is-narrow" v-if="pageSchedules && pageSchedules[index]">
            <a class="button"
              :class="summary(index) ? 'is-info' : 'is-light'"
              title="Zeitplan"
              @click="toggleSchedule(index)">
              <span class="icon"><i class="fas fa-calendar-alt"></i></span>
            </a>
          </div>

          <!-- PAGE REMOVE -->
          <div class="column is-narrow">
            <a class="button is-danger" @click="remove(index)">
              <span class="icon is-small"><i class="fas fa-times"></i></span>
            </a>
          </div>
        </div>

        <!-- PAGE SCHEDULE -->
        <div class="schedule" v-if="openSchedule === index && pageSchedules && pageSchedules[index]">
          <p class="help" style="margin-bottom:15px">
            Ohne gewählte Tage wird die Seite jeden Tag angezeigt, ein Tag ohne Zeitfenster ganztägig.
            Zeiten gelten in österreichischer Zeit, Ende 00:00 = Mitternacht.
          </p>

          <div class="field is-horizontal">
            <label class="label field-label help">Gültig ab</label>
            <div class="field-body">
              <div class="field has-addons">
                <div class="control">
                  <input class="input" type="datetime-local"
                    :value="toLocalInput(pageSchedules[index].validFrom)"
                    @input="pageSchedules[index].validFrom = fromLocalInput($event.target.value)">
                </div>
                <div class="control" v-if="pageSchedules[index].validFrom">
                  <a class="button" @click="pageSchedules[index].validFrom = null">
                    <span class="icon is-small"><i class="fas fa-times"></i></span>
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div class="field is-horizontal">
            <label class="label field-label help">Gültig bis</label>
            <div class="field-body">
              <div class="field has-addons">
                <div class="control">
                  <input class="input" type="datetime-local"
                    :value="toLocalInput(pageSchedules[index].validUntil)"
                    @input="pageSchedules[index].validUntil = fromLocalInput($event.target.value)">
                </div>
                <div class="control" v-if="pageSchedules[index].validUntil">
                  <a class="button" @click="pageSchedules[index].validUntil = null">
                    <span class="icon is-small"><i class="fas fa-times"></i></span>
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div class="columns is-mobile day" v-for="day in days" :key="day.key">
            <div class="column is-3">
              <b-checkbox v-model="pageSchedules[index].days[day.key].enabled">{{ day.label }}</b-checkbox>
            </div>
            <div class="column" v-if="pageSchedules[index].days[day.key].enabled">
              <p class="help" v-if="pageSchedules[index].days[day.key].timeslots.length === 0">Ganztägig</p>
              <div class="field has-addons"
                v-for="(slot, slotIndex) in pageSchedules[index].days[day.key].timeslots"
                :key="slotIndex">
                <div class="control">
                  <input class="input is-small" type="time" v-model="slot.start">
                </div>
                <div class="control"><span class="button is-small is-static">bis</span></div>
                <div class="control">
                  <input class="input is-small" type="time" v-model="slot.end">
                </div>
                <div class="control">
                  <a class="button is-small" @click="removeTimeslot(index, day.key, slotIndex)">
                    <span class="icon is-small"><i class="fas fa-times"></i></span>
                  </a>
                </div>
              </div>
              <a class="button is-small is-text" @click="addTimeslot(index, day.key)">+ Zeitfenster</a>
            </div>
          </div>

          <a class="button is-small is-light" @click="resetSchedule(index)">Zeitplan entfernen</a>
        </div>
      </div>
    </draggable>

    <!-- PAGE MODAL -->
    <div class="modal" :class="{'is-active':modalIsActive}">
      <div class="modal-background"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">Seite wählen</p>
          <button class="delete" @click="closeModal"></button>
        </header>
        <section class="modal-card-body">

          <!-- PAGES THE USER OWN -->
          <p class="title is-5">Deine Seiten</p>
          <p class="subtitle is-7">Seiten, die du selbst erstellt hast.</p>
          <table class="table is-fullwidth is-narrow is-hoverable is-striped">
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(page, index) in pages" @click="update(page)">
                <th>{{ index + 1 }}</th>
                <td>{{ page.meta.name }}</td>
              </tr>
            </tbody>
          </table>

          <hr>
          <!-- PAGES THE USER IS MEMBER -->
          <p class="title is-5">Mitglieds-Seiten</p>
          <p class="subtitle is-7">Seiten, welchen du als Mitglied hinzugefügt wurdest.</p>
          <table class="table is-fullwidth is-narrow is-hoverable is-striped">
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(page, index) in pagesMember" @click="update(page)">
                <th>{{ index + 1 }}</th>
                <td>{{ page.meta.name }}</td>
              </tr>
            </tbody>
          </table>

        </section>
        <footer><div class="modal-card-foot"></div></footer>
      </div>
    </div>

    <!-- TIMER DISPLAY -->
    <p class="subtitle is-5 mt-5"><strong>Zeitanzeige</strong></p>
    <hr>
    <section>

      <b-field>
        <b-switch v-model="timeDisplay.active">Aktiv</b-switch>
      </b-field>

      <div class="columns">
        <div class="column">
          <b-field label="Position">
            <b-select placeholder="Position" v-model="timeDisplay.position">
              <option value="top">Oben</option>
              <option value="bottom">Unten</option>
            </b-select>
          </b-field>
        </div>
        <div class="column">
          <b-field label="Vordergrund Farbe">
            <input type="color" v-model="timeDisplay.foregroundColor" />
          </b-field>
        </div>
        <div class="column">
          <b-field label="Hintergrund Farbe">
            <input type="color" v-model="timeDisplay.backgroundColor" />
          </b-field>
        </div>
      </div>

    </section>

  </div>

</template>

<script>

  import draggable from 'vuedraggable'
  import { Color } from "buefy"
  import { DAYS, emptySchedule, scheduleSummary, isScheduleExpired, isSchedulePending, isScheduleActive } from '@/_helpers/schedule.helper'

  export default {
    // pageSchedules is optional, without it no scheduling ui is shown (e.g. organization pages)
    props: ['value', 'pages', 'pagesMember', 'pageDurations', 'pageSchedules', 'timeDisplay'],
    data() {
      return {
        chosenPages: this.value,
        modalIsActive: false,
        openSchedule: null,
        days: DAYS,
        now: new Date(),
        nowTimer: null
      }
    },
    mounted() {
      // keep active/inactive status of pages current
      this.nowTimer = setInterval(() => { this.now = new Date(); }, 30000);
    },
    beforeDestroy() {
      clearInterval(this.nowTimer);
    },
    components: {
      draggable
    },
    methods: {
      move(arr, oldIndex, newIndex) {
        if (newIndex >= arr.length) {
          let k = newIndex - arr.length + 1;
          while (k--) { arr.push(undefined); }
        }
        arr.splice(newIndex, 0, arr.splice(oldIndex, 1)[0]);
        return arr;
      },
      updateOrder(e) {
        let durations = this.move([...this.pageDurations], e.moved.oldIndex, e.moved.newIndex);
        this.$emit('durations', durations)

        if (this.pageSchedules) {
          let schedules = this.move([...this.pageSchedules], e.moved.oldIndex, e.moved.newIndex);
          this.$emit('schedules', schedules);
        }
        this.openSchedule = null;

        let array = [...this.chosenPages];
        this.$emit('input', array);
      },
      update(page) {
        this.closeModal();
        let array = [...this.chosenPages];
        array.push(page);
        this.chosenPages = array;
        this.$emit('input', array);

        if (this.pageSchedules) {
          this.$emit('schedules', [...this.pageSchedules, emptySchedule()]);
        }
      },
      remove(index) {
        let array = [...this.chosenPages];
        array.splice(index,1);
        this.chosenPages = array;
        this.$emit('input', array);

        let durations = [...this.pageDurations];
        durations.splice(index,1);
        this.$emit('durations', durations);

        if (this.pageSchedules) {
          let schedules = [...this.pageSchedules];
          schedules.splice(index,1);
          this.$emit('schedules', schedules);
        }
        this.openSchedule = null;
      },
      toggleSchedule(index) {
        this.openSchedule = this.openSchedule === index ? null : index;
      },
      addTimeslot(index, day) {
        this.pageSchedules[index].days[day].timeslots.push({ start: '08:00', end: '17:00' });
      },
      removeTimeslot(index, day, slotIndex) {
        this.pageSchedules[index].days[day].timeslots.splice(slotIndex, 1);
      },
      resetSchedule(index) {
        let schedules = [...this.pageSchedules];
        schedules[index] = emptySchedule();
        this.$emit('schedules', schedules);
      },
      summary(index) {
        return scheduleSummary(this.pageSchedules[index]);
      },
      isExpired(index) {
        return isScheduleExpired(this.pageSchedules[index], this.now);
      },
      isPending(index) {
        return isSchedulePending(this.pageSchedules[index], this.now);
      },
      statusClass(index) {
        if (!this.pageSchedules || !this.pageSchedules[index]) { return null; }
        return isScheduleActive(this.pageSchedules[index], this.now) ? 'is-page-active' : 'is-page-inactive';
      },
      // <input type="datetime-local"> works with local 'YYYY-MM-DDTHH:mm' strings
      toLocalInput(date) {
        if (!date) { return ''; }
        const d = new Date(date);
        const pad = (n) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
      },
      fromLocalInput(value) {
        return value ? new Date(value).toISOString() : null;
      },
      openModal() {
        this.modalIsActive = true;
      },
      closeModal() {
        this.modalIsActive = false;
      }
    }
    // watch : {
    //   value(newvalue) {
    //     this.chosenPages = newvalue;
    //   }
    // }
  }
</script>

<style scoped>

  tr { cursor:  pointer }

  .box.is-page-active   { border-left: 6px solid #23d160; }
  .box.is-page-inactive { border-left: 6px solid #ff3860; }

  .schedule {
    margin-top: 15px;
    padding: 15px;
    border-top: 1px solid rgba(0,0,0,0.1);
  }

  .schedule .day {
    margin-bottom: 0;
  }

  .schedule .day .column {
    padding-top: 5px;
    padding-bottom: 5px;
  }

</style>
